import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { generatedApi } from 'redux/services/api.generated';
import { serviceLevelDefinitionsApi } from 'redux/services/generate/configurations/serviceLevelDefinitions';
import { customSubscriptionConfigurationsApi } from 'redux/services/generate/endpoints/customSubscriptionConfigurations';
import { bffUserEndpointName } from 'redux/services/bffUserApi';
import { showErrorToast } from 'redux/slices/messageSlice';

type GeneratedEndpoint = Extract<keyof typeof generatedApi.endpoints, string>;
type ServiceLevelDefinitionEndpoint = Extract<
  keyof typeof serviceLevelDefinitionsApi.endpoints,
  string
>;
type CustomSubscriptionConfigurationEndpoint = Extract<
  keyof typeof customSubscriptionConfigurationsApi.endpoints,
  string
>;
type ApiEndpoint =
  | GeneratedEndpoint
  | ServiceLevelDefinitionEndpoint
  | CustomSubscriptionConfigurationEndpoint;
type Endpoint = ApiEndpoint | typeof bffUserEndpointName;

const endpointNames = <EndpointName extends string>(
  endpoints: Record<EndpointName, unknown>,
) => Object.keys(endpoints) as EndpointName[];

const apiEndpoints: Endpoint[] = [
  ...endpointNames(generatedApi.endpoints),
  ...endpointNames(serviceLevelDefinitionsApi.endpoints),
  ...endpointNames(customSubscriptionConfigurationsApi.endpoints),
  bffUserEndpointName,
];

const unusedEndpointNames = [
  'deactivateSubscriptionLine',
  'discontinueSubscriptionLine',
  'reactivateSubscriptionLine',
  'exportSubscription',
  'exportSubscriptionLines',
  'getServiceLevelDefinitionById',
  'listServiceLevelDefinitionClassifiers',
  bffUserEndpointName,
] as const satisfies readonly Endpoint[];

type UsedEndpoint = Exclude<Endpoint, (typeof unusedEndpointNames)[number]>;

const unusedEndpoints: readonly Endpoint[] = unusedEndpointNames;

const usedEndpoints = new Set<UsedEndpoint>(
  apiEndpoints.filter(
    (endpoint): endpoint is UsedEndpoint => !unusedEndpoints.includes(endpoint),
  ),
);

interface Action extends UnknownAction {
  meta?: {
    arg?: {
      endpointName: UsedEndpoint;
    };
  };
}

const getString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isDefined = <Value>(value: Value | undefined): value is Value =>
  value !== undefined;

const getBackendErrorMessage = (value: unknown) => {
  if (!isRecord(value)) {
    return undefined;
  }

  return getString(value.message) ?? getString(value.errorMessage);
};

const getBackendErrorMessages = (errors: unknown) =>
  Array.isArray(errors) ? errors.map(getBackendErrorMessage).filter(isDefined) : [];

export const getApiErrorMessage = (payload: unknown) => {
  const data = isRecord(payload) && 'data' in payload ? payload.data : payload;

  const directMessage = getBackendErrorMessage(data);

  if (directMessage !== undefined) {
    return directMessage;
  }

  const messages = isRecord(data)
    ? getBackendErrorMessages(data.errors)
    : getBackendErrorMessages(data);

  return messages.length > 0 ? messages.join('\n') : undefined;
};

export const errorHandler: Middleware =
  ({ dispatch }) =>
  (next) =>
  (unknownAction) => {
    // Typecast to resolve TS error.
    const action = unknownAction as Action;
    if (!action.meta?.arg?.endpointName) {
      return next(action);
    }
    const endpointName = action.meta.arg.endpointName;
    if (isRejectedWithValue(action) && usedEndpoints.has(endpointName)) {
      console.error(action.meta.arg.endpointName, action.payload, action.error);
      // This will give a TS error if an endpoint is added, and translation is not added.
      const message =
        getApiErrorMessage(action.payload) ?? t(`error.${endpointName}`);
      dispatch(showErrorToast(message));
    }

    return next(action);
  };
